/**
 * Bidirectional mapping between the Tahiti `pms_word` shape and our domain
 * {@link Word} / {@link WordSummary} types.
 *
 * Reads arrive FLAT (verified against devdb): `meta/list` is already flat, and
 * `api.ts` collapses `meta/object`'s `{value0,value}` wrappers before calling in.
 * Writes use the verified `meta/object` contract: create sends `{value}` fields
 * (with a FLAT `title` — the multilingual column rejects a wrapped title on
 * insert), and update sends `{value0,value}` diffs, emitting a field only when it
 * actually changed (the server skips equal values but bumps the record version).
 */
import { dominantNoyauCode, dominantNoyauKey } from '@/lib/noyaux';
import { extractReasoningSteps, mergeReasoningSteps } from '@/lib/reasoning-steps';
import type { TahitiWord } from '@/schemas/tahiti';
import { fonctionCodeSchema, noyauCodeSchema } from '@/schemas/word';
import type {
  FonctionCode,
  NoyauCode,
  NoyauPonderation,
  ReasoningSteps,
  TagCategory,
  Word,
  WordSummary,
  WordTag,
} from '@/schemas/word';

// ── Param/result types (api.ts imports these) ─────────────────────────────────

/** Fields needed to create a new stub word. */
export type WordCreate = {
  title: string;
  noyauCode?: NoyauCode | null;
  /** Tags to seed on the new word (used by bulk add). Omit for a bare stub. */
  tags?: WordTag[] | undefined;
  /** Ponderation to seed for `noyauCode` (default 1); used by bulk add's batch-vs-batch merge to seed 2 directly. */
  noyauWeight?: number;
  /** Marks the word as created via bulk import (used by bulk add). Omit for a bare stub. */
  imported?: boolean;
};

/** Editable fields of a word; only the present ones are written. */
export type WordPatch = {
  title?: string;
  content?: string;
  tags?: WordTag[];
  seeAlso?: string;
  noyauPonderation?: NoyauPonderation;
  /** Set together when validating/un-validating the noyau (Point 8). */
  noyauSing?: NoyauCode | null;
  noyauValidated?: boolean;
  /** Present only when a reasoning step was actually edited this session (lazy persist, Point 4). */
  reasoningSteps?: ReasoningSteps;
};

/**
 * The current stored values (`value0`) for the editable fields, captured from
 * the detail read and carried into the next save so the `meta/object` PUT can
 * send true diffs (see the grilled write-baseline design). `numericId`+`guid`
 * are the PUT's required query identifiers.
 */
export type WordWriteBaseline = {
  numericId: string;
  guid: string;
  title: string;
  content: string;
  tags: string;
  seeAlso: string;
  ponderation: NoyauPonderation;
  noyauSing: string;
  noyauValidated: boolean;
};

/** The `meta/object` write body shape: `{ table, data: [row] }`. */
type WriteBody = { table: 'pms_word'; data: [Record<string, unknown>] };

/**
 * The blank create template: the full `{value0,value}` record returned by
 * `GET /api/meta/object?obj_code=pms_word`. devdb rejects a hand-built minimal
 * insert body (500 "Insert operation failed"), so a create must overlay its
 * fields onto this complete template and POST the whole thing back.
 */
export type TahitiCreateTemplate = Record<string, unknown>;

/** Ponderation columns in canonical noyau order [SGP, SPG, PSG, PGS, GPS, GSP]. */
const PONDERATION_FIELDS = [
  'ponderation_sgp',
  'ponderation_spg',
  'ponderation_psg',
  'ponderation_pgs',
  'ponderation_gps',
  'ponderation_gsp',
] as const;

// ── Read helpers ──────────────────────────────────────────────────────────────

/** Coerce a flat Tahiti value into a string, or `''` when null/absent. */
function str(raw: string | number | null | undefined): string {
  return typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
}

/** Coerce a flat Tahiti value into a string, or `null` when null/absent. */
function nullableStr(raw: string | number | null | undefined): string | null {
  return raw == null ? null : String(raw);
}

/** Coerce a ponderation cell ("12" / 12 / null) into a number (0 when absent/NaN). */
function num(raw: string | number | null | undefined): number {
  if (raw == null) return 0;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Slugify a title for cosmetic use (URLs/links): lowercase, strip accents,
 * spaces→dash, drop non-url chars.
 */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Normalize a Postgres-style timestamp (e.g. "2026-01-02 15:04:05.123+00") into
 * a valid ISO-8601 datetime string. Falls back to now when unparseable/empty.
 */
function normalizeTimestamp(raw: string): string {
  if (!raw) return new Date().toISOString();
  let s = raw.includes('T') ? raw : raw.replace(' ', 'T');
  if (s.includes('T')) {
    s = s.replace(/([+-]\d{2})(\d{2})?$/, (_m, h: string, m?: string) => `${h}:${m ?? '00'}`);
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return new Date().toISOString();
  return parsed.toISOString();
}

/** Validate a raw noyau string into a {@link NoyauCode}, or null ("???"/unknown). */
function parseNoyauCode(raw: string): NoyauCode | null {
  const parsed = noyauCodeSchema.safeParse(raw.trim());
  return parsed.success ? parsed.data : null;
}

/**
 * Validate a raw fonction string into a {@link FonctionCode} (G/P/S), or null
 * for unset/unexpected values. `fonction` is algorithm-owned and not validated
 * at the DB, so this is read-tolerant rather than throwing.
 */
function parseFonctionCode(raw: string): FonctionCode | null {
  const parsed = fonctionCodeSchema.safeParse(raw.trim());
  return parsed.success ? parsed.data : null;
}

/** Coerce a Tahiti boolean cell ("true"/"false"/true/null) into a boolean (false when absent). */
function parseBool(raw: string | boolean | null | undefined): boolean {
  return raw === true || raw === 'true';
}

/** Split a CSV tag string ("#expression, #sgp") into {@link WordTag}s. */
function parseTags(raw: string): WordTag[] {
  return raw
    .split(',')
    .map((part) => part.trim().replace(/^#/, '').trim())
    .filter((label) => label.length > 0)
    .map((label) => ({ label }));
}

/** Read the six ponderation columns into a {@link NoyauPonderation} tuple. */
function ponderationFromItem(item: TahitiWord): NoyauPonderation {
  return [
    num(item.ponderation_sgp),
    num(item.ponderation_spg),
    num(item.ponderation_psg),
    num(item.ponderation_pgs),
    num(item.ponderation_gps),
    num(item.ponderation_gsp),
  ];
}

// ── Read mappers ──────────────────────────────────────────────────────────────

/**
 * The stable numeric primary key Tahiti uses for writes. The domain `Word` keys
 * on the guid, so callers issuing a `PUT .../meta/object?id=<n>` read it here.
 */
export function tahitiNumericId(item: TahitiWord): string {
  return str(item.id);
}

/** Map a flat Tahiti `pms_word` item to a full domain {@link Word}. */
export function toWord(item: TahitiWord): Word {
  const title = str(item.title);
  const updatedAt = normalizeTimestamp(str(item.updated_at));
  const createdAt = item.created_at ? normalizeTimestamp(str(item.created_at)) : updatedAt;
  // Strip the hidden Point-4 reasoning-steps block before it ever reaches the
  // editor, so it never leaks into the body as literal fence-marker text.
  const { content: bodyMarkdown, steps: reasoningSteps } = extractReasoningSteps(str(item.content));

  return {
    id: str(item.guid),
    numericId: tahitiNumericId(item),
    slug: slugify(title),
    title,
    noyauPonderation: ponderationFromItem(item),
    noyauSing: parseNoyauCode(str(item.noyau_sing)),
    noyauValidated: parseBool(item.noyau_validated),
    tags: parseTags(str(item.tags)),
    content: bodyMarkdown,
    reasoningSteps,
    seeAlso: str(item.see_also),
    fonction: parseFonctionCode(str(item.fonction)),
    genre: nullableStr(item.genre),
    toCheck: parseBool(item.to_check),
    reasons: nullableStr(item.reasons),
    revisedAt: updatedAt.slice(0, 10),
    createdAt,
    updatedAt,
  };
}

/** Map a flat Tahiti `pms_word` item to a lightweight {@link WordSummary}. */
export function toWordSummary(item: TahitiWord): WordSummary {
  const title = str(item.title);
  const ponderation = ponderationFromItem(item);
  const noyauSing = parseNoyauCode(str(item.noyau_sing));

  return {
    id: str(item.guid),
    numericId: tahitiNumericId(item),
    slug: slugify(title),
    title,
    noyauPonderation: ponderation,
    fonction: parseFonctionCode(str(item.fonction)),
    toCheck: parseBool(item.to_check),
    reasons: nullableStr(item.reasons),
    dominantNoyauCode: dominantNoyauCode(ponderation, noyauSing),
    dominantNoyauKey: dominantNoyauKey(ponderation, noyauSing),
  };
}

/** Capture the write baseline (current stored values) from a loaded item. */
export function toWriteBaseline(item: TahitiWord): WordWriteBaseline {
  return {
    numericId: str(item.id),
    guid: str(item.guid),
    title: str(item.title),
    content: str(item.content),
    tags: str(item.tags),
    seeAlso: str(item.see_also),
    ponderation: ponderationFromItem(item),
    noyauSing: str(item.noyau_sing),
    noyauValidated: parseBool(item.noyau_validated),
  };
}

// ── Write builders ────────────────────────────────────────────────────────────

/** Serialize {@link WordTag}s back into a Tahiti CSV string ("#a, #b"). */
function tagsToCsv(tags: WordTag[]): string {
  return tags.map((tag) => `#${tag.label}`).join(', ');
}

/**
 * Overlay `fields` (plain values) onto a blank create `template`'s `.value`
 * cells, preserving each cell's shape (`value0`, …). devdb 500s on a minimal
 * hand-built insert body ("Insert operation failed — Cannot read properties of
 * undefined (reading 'sql')"): every column must be present as a
 * `{value0,value}` cell, so every create body builder clones the full blank
 * template and overlays only the fields it wants — verified end-to-end on
 * devdb 2026-06-29 for `pms_word` (title/tags/ponderation all persist).
 */
export function overlayCreateTemplate(
  template: TahitiCreateTemplate,
  fields: Record<string, unknown>,
): Record<string, unknown> {
  const row: Record<string, unknown> = structuredClone(template);
  for (const [field, value] of Object.entries(fields)) {
    const cell = row[field];
    row[field] =
      cell !== null && typeof cell === 'object' && !Array.isArray(cell)
        ? { ...(cell as Record<string, unknown>), value }
        : { value0: null, value };
  }
  return row;
}

/**
 * Build the create body for `POST /api/meta/object?table=pms_word`.
 *
 * The chosen noyau is recorded by seeding its weight (`ponderation_<code> = 1`),
 * NOT by writing `noyau_sing`: that controlled-value column 500s on a meta/object
 * write, so we never touch it. The dominant noyau is then derived from the
 * ponderation (see {@link dominantNoyauCode}).
 */
export function toTahitiCreateBody(data: WordCreate, template: TahitiCreateTemplate): WriteBody {
  const fields: Record<string, unknown> = { title: data.title };
  if (data.noyauCode) fields[`ponderation_${data.noyauCode.toLowerCase()}`] = data.noyauWeight ?? 1;
  if (data.tags && data.tags.length > 0) fields.tags = tagsToCsv(data.tags);
  if (data.imported) fields.imported = true;

  return { table: 'pms_word', data: [overlayCreateTemplate(template, fields)] };
}

/** Build the create body for `POST /api/meta/object?table=pms_tag` (Point 6 tag creation). */
export function toTahitiTagCreateBody(
  label: string,
  category: TagCategory | null,
  template: TahitiCreateTemplate,
): { table: 'pms_tag'; data: [Record<string, unknown>] } {
  const fields: Record<string, unknown> = { title: label };
  if (category) fields.tag_category = category;

  return { table: 'pms_tag', data: [overlayCreateTemplate(template, fields)] };
}

/**
 * Build the update body for `PUT /api/meta/object?table=pms_word&id=&guid=`.
 * Each present, *changed* field is emitted as `{value0,value}`; unchanged fields
 * (and the whole tuple of unchanged ponderations) are omitted so the server's
 * diff-write does the minimal update.
 */
export function toTahitiUpdateBody(patch: WordPatch, baseline: WordWriteBaseline): WriteBody {
  const row: Record<string, unknown> = { id: { value: baseline.numericId } };

  const diff = (field: string, value0: string, value: string) => {
    if (value !== value0) row[field] = { value0, value };
  };

  if (patch.title !== undefined) diff('title', baseline.title, patch.title);
  if (patch.content !== undefined || patch.reasoningSteps !== undefined) {
    // Lazy persist (Point 4): only fold the hidden reasoning-steps block into
    // `content` when the word already had one, or a step was actually edited
    // this session (patch.reasoningSteps present) — never inject it just
    // because an unrelated body edit is being saved.
    const baselineExtract = extractReasoningSteps(baseline.content);
    const bodyMarkdown = patch.content !== undefined ? patch.content : baselineExtract.content;
    const steps = patch.reasoningSteps ?? baselineExtract.steps;
    const includeBlock = baselineExtract.hadBlock || patch.reasoningSteps !== undefined;
    const nextContent = includeBlock ? mergeReasoningSteps(bodyMarkdown, steps) : bodyMarkdown;
    diff('content', baseline.content, nextContent);
  }
  if (patch.tags !== undefined) diff('tags', baseline.tags, tagsToCsv(patch.tags));
  if (patch.seeAlso !== undefined) diff('see_also', baseline.seeAlso, patch.seeAlso);
  if (patch.noyauPonderation !== undefined) {
    const next = patch.noyauPonderation;
    PONDERATION_FIELDS.forEach((field, i) => {
      diff(field, String(baseline.ponderation[i] ?? 0), String(next[i] ?? 0));
    });
  }
  // NOTE: unlike the create path (toTahitiCreateBody), writing `noyau_sing` on
  // an UPDATE has not been independently verified against live devdb — only
  // the create-path 500 is confirmed. Point 8 (noyau validation) requires this
  // write; if devdb rejects it too, the validate/un-validate flow needs a
  // fallback (e.g. ponderation-only, matching the create workaround).
  if (patch.noyauSing !== undefined) diff('noyau_sing', baseline.noyauSing, patch.noyauSing ?? '');
  if (patch.noyauValidated !== undefined) {
    diff('noyau_validated', String(baseline.noyauValidated), String(patch.noyauValidated));
  }

  return { table: 'pms_word', data: [row] };
}
